// A20 W8 — the walkthrough library's data layer.
//
// Row shape mirrors `supabase/migrations/34_help_assets.sql` and the service's
// PUBLIC_COLUMNS (backend/src/services/help-assets.service.ts) column for
// column, in the same hand-kept-in-lockstep idiom as ./api.ts and lib/api/trust.ts.
//
// ── ONE COLUMN IS MISSING ON PURPOSE ────────────────────────────────────────
// `recording_path`. It is a storage object path; migration 34 and the service
// both state the rule outright, and PUBLIC_COLUMNS omits it so the browser
// cannot receive it even by accident. Bytes reach a reviewer only as bytes, from
// our own origin, which is what keeps the storage host undisclosed. If a path or
// a signed URL ever appears in this file, that requirement has been broken.
//
// ── TWO FLAGS THAT ARE NOT THE SAME FLAG ────────────────────────────────────
// `capture_received` = the browser sent bytes with the save.
// `capture_available` = those bytes are RETRIEVABLE, i.e. a path exists.
// The DB refuses `capture_available` without a path (help_assets_capture_path_ck),
// so the pair is the honest answer to "can this actually be replayed?". The page
// renders them as different states because they are different facts — and the
// difference is exactly why a draft can fail to publish.

import { apiRequest, ApiError } from '@/lib/supabase';
import { fetchReplayEvents, type SurfaceKey } from './api';

export interface HelpAssetRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  surface_key: SurfaceKey;
  topic: string | null;
  route: string | null;
  locale: string | null;
  masked: boolean;
  duration_ms: number | null;
  capture_received: boolean;
  capture_available: boolean;
  recording_bytes: number | null;
  author_user_id: string | null;
  author_email: string | null;
  author_role: string | null;
  author_org_id: string | null;
  published: boolean;
  published_at: string | null;
  published_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Accept the `{ data }` envelope the route actually sends and REFUSE anything
 * else. Same posture as ./api.ts's unwrapList: guessing would turn a contract
 * mismatch into a silent empty state, and "no walkthroughs" and "I could not
 * read the answer" must never render the same.
 */
function unwrapList(payload: unknown): HelpAssetRow[] {
  if (Array.isArray(payload)) return payload as HelpAssetRow[];
  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown }).data;
    if (Array.isArray(data)) return data as HelpAssetRow[];
  }
  throw new ApiError(
    'The licence server returned an unrecognised shape for GET /help-assets.',
    502,
  );
}

function unwrapOne(payload: unknown): HelpAssetRow {
  const row = (payload && typeof payload === 'object' && 'data' in (payload as object)
    ? (payload as { data: unknown }).data
    : payload) as HelpAssetRow | null;
  if (!row || typeof row !== 'object' || typeof row.id !== 'string') {
    throw new ApiError(
      'The licence server returned an unrecognised shape for a walkthrough.',
      502,
    );
  }
  return row;
}

/** The authoring library, newest first. requireAdmin on the server. */
export async function listWalkthroughs(limit = 100): Promise<HelpAssetRow[]> {
  const q = new URLSearchParams({ limit: String(limit) });
  return unwrapList(await apiRequest<unknown>(`/help-assets?${q.toString()}`));
}

/**
 * Publish or un-publish.
 *
 * The server refuses to publish an asset with no stored recording (409
 * HELP_ASSET_NO_RECORDING) and re-runs assertStudioAuthor on the verified
 * session for both directions. The page disables the control when it can see
 * that will happen, but the refusal is the server's and its message is shown
 * verbatim — the button being enabled is never what makes the write legal.
 */
export async function setWalkthroughPublished(
  id: string,
  published: boolean,
): Promise<HelpAssetRow> {
  return unwrapOne(
    await apiRequest<unknown>(`/help-assets/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ published }),
    }),
  );
}

/**
 * Fetch and rehydrate one walkthrough's recording.
 *
 * The bytes come from OUR origin through GET /help-assets/:id/recording — the
 * same shape, and the same one shared inflater, as the bug replay. There is no
 * storage URL in this call and there can never be one: the only thing this
 * function knows is a walkthrough id, which is exactly what makes the storage
 * location undisclosed rather than merely undisplayed.
 *
 * Failures are ApiError with the server's status, so the caller can keep telling
 * the four apart: 403 is the draft fence (this walkthrough is unpublished and
 * you are not a studio author), 404/410 mean there are no bytes, and anything
 * else is a real fault.
 */
export async function fetchWalkthroughEvents(id: string): Promise<unknown[]> {
  return fetchReplayEvents(`/help-assets/${encodeURIComponent(id)}/recording`);
}
